import { AxiosError, AxiosHeaders, type AxiosAdapter, type InternalAxiosRequestConfig } from "axios";
import { API_URL } from "../../helpers/api";

/**
 * A tiny in-browser API: a route table answered from in-memory sample data, plugged into
 * axios as its adapter while a tutorial runs. Nothing here touches the network, so a
 * tutorial cannot reach the real API - or the database behind it - by construction.
 */

export interface MockRequest {
  method: string;
  /** Path relative to the API root, no query string ("/mrqs/9702"). */
  path: string;
  /** Pattern captures (":id") - always strings, exactly as a URL carries them. */
  params: Record<string, string>;
  /** Query string values, from `params` on the axios config and from the URL itself. */
  query: Record<string, string>;
  body: unknown;
}

export type MockHandler = (req: MockRequest) => unknown;

/** Thrown by a handler to answer with an HTTP error the way the Go server would (JSONError). */
export class MockError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

/** Fired on `window` after every request the mock server answers - what "api" step triggers listen to. */
export const TUTORIAL_API_EVENT = "glut:tutorial-api";

export interface TutorialApiEventDetail {
  method: string;
  path: string;
  ok: boolean;
}

/** Enough delay for spinners and loading states to look the way they do against the real API. */
const LATENCY_MS = 150;

interface Route {
  method: string;
  pattern: string;
  handler: MockHandler;
}

/** Matches one path against a ":param" pattern; returns the captures or null. */
export function matchPath(pattern: string, path: string): Record<string, string> | null {
  const want = pattern.split("/").filter(Boolean);
  const have = path.split("/").filter(Boolean);
  if (want.length !== have.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < want.length; i++) {
    if (want[i].startsWith(":")) params[want[i].slice(1)] = decodeURIComponent(have[i]);
    else if (want[i] !== have[i]) return null;
  }
  return params;
}

export class MockRouter {
  private routes: Route[] = [];

  on(method: string, pattern: string, handler: MockHandler): this {
    this.routes.push({ method: method.toUpperCase(), pattern, handler });
    return this;
  }

  get(pattern: string, handler: MockHandler) {
    return this.on("GET", pattern, handler);
  }
  post(pattern: string, handler: MockHandler) {
    return this.on("POST", pattern, handler);
  }
  put(pattern: string, handler: MockHandler) {
    return this.on("PUT", pattern, handler);
  }
  delete(pattern: string, handler: MockHandler) {
    return this.on("DELETE", pattern, handler);
  }

  dispatch(req: MockRequest): unknown {
    for (const route of this.routes) {
      if (route.method !== req.method) continue;
      const params = matchPath(route.pattern, req.path);
      if (params) return route.handler({ ...req, params });
    }
    // Loud in the console: a page reached for an endpoint the guide does not stage, which
    // is exactly what a developer extending a page needs to see. Quiet in the UI: the
    // message reads like any other server error.
    console.warn(`[tutorial] no sample data for ${req.method} ${req.path}`);
    throw new MockError(404, "That isn't part of this tutorial.");
  }
}

/** Splits an axios request into the method/path/query/body a handler works with. */
function toMockRequest(config: InternalAxiosRequestConfig): MockRequest {
  // config.url is what the caller passed ("/mrqs/1" or, rarely, the full origin form);
  // strip the API root either way so route patterns are always root-relative.
  const url = new URL(config.url ?? "", "http://tutorial.local");
  let path = url.pathname;
  const apiPath = new URL(API_URL, "http://tutorial.local").pathname.replace(/\/$/, "");
  if (apiPath && path.startsWith(apiPath + "/")) path = path.slice(apiPath.length);
  const query: Record<string, string> = {};
  url.searchParams.forEach((v, k) => (query[k] = v));
  for (const [k, v] of Object.entries((config.params as Record<string, unknown> | undefined) ?? {})) {
    if (v !== undefined && v !== null) query[k] = String(v);
  }
  let body: unknown = config.data;
  if (typeof body === "string") {
    try {
      body = JSON.parse(body);
    } catch {
      /* not JSON - hand it over as is */
    }
  }
  return { method: (config.method ?? "get").toUpperCase(), path, params: {}, query, body };
}

function announce(detail: TutorialApiEventDetail) {
  window.dispatchEvent(new CustomEvent<TutorialApiEventDetail>(TUTORIAL_API_EVENT, { detail }));
}

/** The axios adapter that serves every request from the router while a tutorial runs. */
export function createMockAdapter(router: MockRouter): AxiosAdapter {
  return async (config) => {
    const req = toMockRequest(config);
    await new Promise((resolve) => setTimeout(resolve, LATENCY_MS));
    const headers = new AxiosHeaders({ "content-type": "application/json" });
    try {
      const result = router.dispatch(req);
      // Serialise the way a wire response would be: the page gets its own copy and cannot
      // reach back into the sample data, and `undefined` fields vanish like they would.
      const data = result === undefined ? null : JSON.parse(JSON.stringify(result));
      announce({ method: req.method, path: req.path, ok: true });
      return { data, status: 200, statusText: "OK", headers, config, request: {} };
    } catch (err) {
      const status = err instanceof MockError ? err.status : 500;
      const message = err instanceof Error ? err.message : "Something went wrong in the tutorial.";
      announce({ method: req.method, path: req.path, ok: false });
      // The same shape helpers/api.ts errorMessage() reads off a real JSONError response.
      const response = { data: { error: message }, status, statusText: message, headers, config, request: {} };
      throw new AxiosError(message, status >= 500 ? AxiosError.ERR_BAD_RESPONSE : AxiosError.ERR_BAD_REQUEST, config, {}, response);
    }
  };
}

// ---- helpers shared by the sample-data worlds ----

interface PaginateOptions<T> {
  searchable?: (row: T) => (string | undefined)[];
  sortable?: Record<string, (row: T) => string | number | undefined>;
}

/** Pages one array the way api.go's Paginated[T] does: page/pageSize/search/sort/order from the query. */
export function paginate<T>(rows: T[], query: Record<string, string>, opts: PaginateOptions<T> = {}) {
  let out = rows;
  const search = (query.search ?? "").trim().toLowerCase();
  if (search && opts.searchable) {
    const fields = opts.searchable;
    out = out.filter((r) => fields(r).some((f) => (f ?? "").toLowerCase().includes(search)));
  }
  const get = query.sort ? opts.sortable?.[query.sort] : undefined;
  if (get) {
    const dir = (query.order ?? "asc").toLowerCase() === "desc" ? -1 : 1;
    out = [...out].sort((a, b) => {
      const av = get(a) ?? "";
      const bv = get(b) ?? "";
      return av < bv ? -dir : av > bv ? dir : 0;
    });
  }
  const page = Math.max(1, Number(query.page) || 1);
  const pageSize = Math.max(1, Math.min(200, Number(query.pageSize) || 20));
  const start = (page - 1) * pageSize;
  return { data: out.slice(start, start + pageSize), count: out.length, meta: { page, pageSize } };
}

export function notFound(what: string): never {
  throw new MockError(404, `${what} not found`);
}

export function requireId(req: Pick<MockRequest, "params">, name = "id"): number {
  const n = Number(req.params[name]);
  if (!Number.isFinite(n)) throw new MockError(400, `invalid ${name}`);
  return n;
}
